/**
 * Upload Service - Handles chunked file uploads with progress tracking
 *
 * Features:
 * - SHA-256 file hashing (Web Crypto API)
 * - Chunked upload (10MB chunks)
 * - Resumable uploads
 * - Progress callbacks
 */

import axios from 'axios';

const BASE_URL = process.env.REACT_APP_BE_MAIN_URL;
const CHUNK_SIZE = 10 * 1024 * 1024; // 10MB

// Updated API endpoints
const UPLOAD_INIT_API = `${BASE_URL}/ocr/upload/init/`;
const UPLOAD_CHUNK_API = `${BASE_URL}/ocr/upload/chunk/`;
const UPLOAD_FINALIZE_API = `${BASE_URL}/ocr/upload/finalize/`;
const STATUS_API = `${BASE_URL}/ocr/status/`;

/**
 * Calculate SHA-256 hash of a file using Web Crypto API
 * @param {File} file - File to hash
 * @param {Function} onProgress - Progress callback (percent)
 * @returns {Promise<string>} Hex hash string
 */
export async function calculateFileHash(file, onProgress) {
  const chunkSize = 2 * 1024 * 1024; // 2MB chunks for hashing
  const chunks = Math.ceil(file.size / chunkSize);
  const hashBuffer = [];

  for (let i = 0; i < chunks; i++) {
    const start = i * chunkSize;
    const end = Math.min(start + chunkSize, file.size);
    const chunk = file.slice(start, end);

    const arrayBuffer = await chunk.arrayBuffer();
    hashBuffer.push(new Uint8Array(arrayBuffer));

    if (onProgress) {
      onProgress(Math.round(((i + 1) / chunks) * 100));
    }
  }

  // Combine all chunks
  const totalLength = hashBuffer.reduce((acc, arr) => acc + arr.length, 0);
  const combined = new Uint8Array(totalLength);
  let offset = 0;
  for (const arr of hashBuffer) {
    combined.set(arr, offset);
    offset += arr.length;
  }

  // Calculate hash
  const hashArrayBuffer = await crypto.subtle.digest('SHA-256', combined);
  const hashArray = Array.from(new Uint8Array(hashArrayBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Initialize upload session
 * @param {Object} params - Upload parameters
 * @returns {Promise<Object>} Session data
 */
export async function initUpload({ filename, fileSize, fileHash, folderName, levels, token }) {
  const response = await axios.post(
    UPLOAD_INIT_API,
    {
      folder_name: folderName,
      filename: filename,
      file_hash: fileHash,
      file_size: fileSize,
      ...levels
    },
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );

  return response.data;
}

/**
 * Upload a single chunk
 * @param {Object} params - Chunk parameters
 * @returns {Promise<Object>} Chunk upload result
 */
export async function uploadChunk({ sessionId, chunkIndex, chunk, token }) {
  const formData = new FormData();
  formData.append('session_id', sessionId);
  formData.append('chunk_index', chunkIndex);
  formData.append('chunk', chunk);

  const response = await axios.post(
    UPLOAD_CHUNK_API,
    formData,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'multipart/form-data'
      }
    }
  );

  return response.data;
}

/**
 * Finalize upload and start processing
 * @param {Object} params - Finalization parameters
 * @param {string|Array<string>} params.sessionId - Single session_id or array of session_ids for batch
 * @returns {Promise<Object>} Task info with task_id
 */
export async function finalizeUpload({ sessionId, token }) {
  const payload = Array.isArray(sessionId) 
    ? { session_ids: sessionId }  // Multiple files - batch processing
    : { session_id: sessionId };   // Single file

  const response = await axios.post(
    UPLOAD_FINALIZE_API,
    payload,
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );

  return response.data;
}

/**
 * Upload file with chunking and progress tracking
 * @param {Object} params - Upload parameters
 * @param {File} params.file - File to upload
 * @param {string} params.folderName - Project folder name
 * @param {Object} params.levels - Space path levels (level1-level8)
 * @param {string} params.token - Auth token
 * @param {Function} params.onProgress - Progress callback
 * @returns {Promise<Object>} Upload result with task_id
 */
export async function uploadFileChunked({
  file,
  folderName,
  levels,
  token,
  onProgress
}) {
  // Phase 1: Hashing
  onProgress({
    phase: 'hashing',
    percent: 0,
    message: 'Calculating file hash...',
    bytesUploaded: 0,
    totalBytes: file.size
  });

  const fileHash = await calculateFileHash(file, (hashPercent) => {
    onProgress({
      phase: 'hashing',
      percent: hashPercent,
      message: `Calculating hash... ${hashPercent}%`,
      bytesUploaded: 0,
      totalBytes: file.size
    });
  });

  // Phase 2: Initialize session
  onProgress({
    phase: 'uploading',
    percent: 0,
    message: 'Initializing upload session...',
    bytesUploaded: 0,
    totalBytes: file.size
  });

  const session = await initUpload({
    filename: file.name,
    fileSize: file.size,
    fileHash,
    folderName,
    levels,
    token
  });

  // Check for duplicate
  if (session.is_duplicate) {
    onProgress({
      phase: 'uploading',
      percent: 100,
      message: 'File already exists (using cached data)',
      isDuplicate: true,
      bytesUploaded: file.size,
      totalBytes: file.size
    });
  }

  // Calculate chunks to upload (skip already uploaded for resume)
  const totalChunks = session.total_chunks;
  const uploadedChunks = new Set(session.chunks_uploaded);

  // Phase 3: Upload chunks
  let bytesUploaded = uploadedChunks.size * session.chunk_size;

  for (let i = 0; i < totalChunks; i++) {
    if (uploadedChunks.has(i)) {
      continue; // Skip already uploaded chunks (resume)
    }

    const start = i * session.chunk_size;
    const end = Math.min(start + session.chunk_size, file.size);
    const chunk = file.slice(start, end);

    const startTime = Date.now();

    await uploadChunk({
      sessionId: session.session_id,
      chunkIndex: i,
      chunk,
      token
    });

    bytesUploaded += chunk.size;
    const elapsedSeconds = (Date.now() - startTime) / 1000;
    const speed = (chunk.size / (1024 * 1024)) / elapsedSeconds;

    onProgress({
      phase: 'uploading',
      percent: Math.round((bytesUploaded / file.size) * 100),
      message: `Uploading chunk ${i + 1}/${totalChunks}`,
      bytesUploaded,
      totalBytes: file.size,
      currentChunk: i + 1,
      totalChunks,
      speed: speed.toFixed(2)
    });
  }

  // Phase 4: Finalize
  onProgress({
    phase: 'processing',
    percent: 100,
    message: 'Finalizing upload...',
    bytesUploaded: file.size,
    totalBytes: file.size
  });

  const result = await finalizeUpload({
    sessionId: session.session_id,
    token
  });

  return {
    ...result,
    sessionId: session.session_id
  };
}

/**
 * Poll task status
 * @param {string} taskId - Celery task ID
 * @param {string} token - Auth token
 * @returns {Promise<Object>} Task status
 */
export async function getTaskStatus(taskId, token) {
  const response = await axios.get(
    `${STATUS_API}${taskId}/`,
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );

  return response.data;
}

/**
 * Upload multiple files with chunking and unified processing
 * All files upload separately, then finalize together to get ONE task_id with unified logs
 * @param {Object} params - Upload parameters
 * @param {Array<File>} params.files - Array of files to upload
 * @param {string} params.folderName - Project folder name
 * @param {Object} params.levels - Space path levels (level1-level8)
 * @param {string} params.token - Auth token
 * @param {Function} params.onProgress - Progress callback
 * @returns {Promise<Object>} Upload result with single task_id for all files
 */
export async function uploadMultipleFilesChunked({ files, folderName, levels, token, onProgress }) {
  const sessionIds = [];
  const totalFiles = files.length;
  let completedFiles = 0;

  // Upload all files and collect session_ids
  for (const file of files) {
    try {
      // Track progress per file
      const fileProgress = (progress) => {
        if (onProgress) {
          onProgress({
            ...progress,
            currentFile: completedFiles + 1,
            totalFiles,
            fileName: file.name,
            message: `[${completedFiles + 1}/${totalFiles}] ${file.name}: ${progress.message}`
          });
        }
      };

      // Phase 1: Hash file
      const fileHash = await calculateFileHash(file, (hashPercent) => {
        fileProgress({
          phase: 'hashing',
          percent: hashPercent,
          message: `Calculating hash... ${hashPercent}%`,
          bytesUploaded: 0,
          totalBytes: file.size
        });
      });

      // Phase 2: Initialize session
      fileProgress({
        phase: 'uploading',
        percent: 0,
        message: 'Initializing upload session...',
        bytesUploaded: 0,
        totalBytes: file.size
      });

      const session = await initUpload({
        filename: file.name,
        fileSize: file.size,
        fileHash,
        folderName,
        levels,
        token
      });

      // Check for duplicate
      if (session.is_duplicate) {
        fileProgress({
          phase: 'uploading',
          percent: 100,
          message: 'File already exists (using cached data)',
          isDuplicate: true,
          bytesUploaded: file.size,
          totalBytes: file.size
        });
      } else {
        // Phase 3: Upload chunks
        const totalChunks = session.total_chunks;
        const uploadedChunks = new Set(session.chunks_uploaded);
        let bytesUploaded = uploadedChunks.size * session.chunk_size;

        for (let i = 0; i < totalChunks; i++) {
          if (uploadedChunks.has(i)) continue;

          const start = i * session.chunk_size;
          const end = Math.min(start + session.chunk_size, file.size);
          const chunk = file.slice(start, end);

          await uploadChunk({
            sessionId: session.session_id,
            chunkIndex: i,
            chunk,
            token
          });

          bytesUploaded += chunk.size;

          fileProgress({
            phase: 'uploading',
            percent: Math.round((bytesUploaded / file.size) * 100),
            message: `Uploading chunk ${i + 1}/${totalChunks}`,
            bytesUploaded,
            totalBytes: file.size,
            currentChunk: i + 1,
            totalChunks
          });
        }
      }

      // Store session_id for batch finalization
      sessionIds.push(session.session_id);
      completedFiles++;

      fileProgress({
        phase: 'uploaded',
        percent: 100,
        message: 'Upload complete',
        bytesUploaded: file.size,
        totalBytes: file.size
      });

    } catch (error) {
      console.error(`Failed to upload ${file.name}:`, error);
      throw new Error(`Failed to upload ${file.name}: ${error.message}`);
    }
  }

  // Phase 4: Finalize ALL files together - returns ONE task_id
  if (onProgress) {
    onProgress({
      phase: 'processing',
      percent: 100,
      message: `Finalizing ${totalFiles} files...`,
      currentFile: totalFiles,
      totalFiles
    });
  }

  const result = await finalizeUpload({
    sessionId: sessionIds,  // Pass array of session_ids
    token
  });

  return {
    ...result,
    sessionIds,  // Return all session_ids for reference
    filesCount: totalFiles
  };
}

/**
 * 12-25 Assembly Approval
 * Approve the reconstructed BOM tree and trigger final assembly classification
 *
 * @param {Object} params - Approval parameters
 * @param {Array<string>} params.fileIds - List of document IDs
 * @param {Object} params.assemblyTree - The approved assembly tree structure
 * @param {Array} params.corrections - Optional user corrections to the tree
 * @param {string} params.token - Auth token
 * @returns {Promise<Object>} New task info with task_id for classification
 */
export async function approveAssembly({ fileIds, assemblyTree, corrections = [], token }) {
  const response = await axios.post(
    `${BASE_URL}/ocr/approve-assembly/`,
    {
      file_ids: fileIds,
      assembly_tree: assemblyTree,
      corrections: corrections
    },
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );

  return response.data;
}

/**
 * 12-25 Get Assembly Tree
 * Get the reconstructed assembly tree for a task in PENDING_APPROVAL state
 *
 * @param {string} taskId - Original analysis task ID
 * @param {string} token - Auth token
 * @returns {Promise<Object>} Assembly tree data
 */
export async function getAssemblyTree(taskId, token) {
  const response = await axios.get(
    `${BASE_URL}/ocr/assembly-tree/${taskId}/`,
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );

  return response.data;
}
